/**
 * Kanonische JSON-Serialisierung.
 *
 * Grundlage der Signaturbindung (CLAUDE.md §3.1): Die Signatur bindet an den
 * Hash der Protokolldaten, nicht an das PDF. Damit derselbe Datenstand auf
 * jedem Gerät und in jeder Lambda-Laufzeit denselben Hash ergibt, muss die
 * Serialisierung vollständig deterministisch sein:
 *
 * - Objekt-Schlüssel werden nach Unicode-Codepoint sortiert
 * - Array-Reihenfolge bleibt erhalten (sie ist fachlich bedeutsam)
 * - keine Leerzeichen, keine Zeilenumbrüche
 * - `undefined` und Funktionen werden weggelassen, `null` bleibt erhalten
 * - `Date` wird als ISO-8601-UTC-String geschrieben
 * - `NaN`/`Infinity` sind ein Fehler, kein stilles `null`
 */

export class CanonicalJsonError extends Error {
  readonly pfad: string;

  constructor(message: string, pfad: string) {
    super(`${message} (Pfad: ${pfad || '<wurzel>'})`);
    this.name = 'CanonicalJsonError';
    this.pfad = pfad;
  }
}

/** Werte, die kanonisch serialisiert werden können. */
export type CanonicalValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | Date
  | CanonicalValue[]
  | { [key: string]: CanonicalValue };

const istEinfachesObjekt = (wert: unknown): wert is Record<string, unknown> =>
  typeof wert === 'object' &&
  wert !== null &&
  (Object.getPrototypeOf(wert) === Object.prototype || Object.getPrototypeOf(wert) === null);

/**
 * JSON-String-Escaping nach RFC 8259. `JSON.stringify` erledigt das bereits
 * deterministisch und identisch in allen Engines, deshalb nutzen wir es hier.
 */
const schreibeString = (wert: string): string => JSON.stringify(wert);

const schreibeZahl = (wert: number, pfad: string): string => {
  if (!Number.isFinite(wert)) {
    throw new CanonicalJsonError(
      `Zahl ist nicht endlich (${String(wert)}) und kann nicht kanonisch serialisiert werden`,
      pfad,
    );
  }
  // -0 und 0 müssen denselben Hash ergeben.
  if (Object.is(wert, -0)) return '0';
  return String(wert);
};

const serialisiere = (wert: unknown, pfad: string, gesehen: Set<object>): string | undefined => {
  if (wert === null) return 'null';
  if (wert === undefined) return undefined;

  const typ = typeof wert;

  if (typ === 'string') return schreibeString(wert as string);
  if (typ === 'number') return schreibeZahl(wert as number, pfad);
  if (typ === 'boolean') return (wert as boolean) ? 'true' : 'false';
  if (typ === 'bigint') {
    throw new CanonicalJsonError('BigInt wird nicht unterstützt', pfad);
  }
  if (typ === 'function' || typ === 'symbol') return undefined;

  if (wert instanceof Date) {
    if (Number.isNaN(wert.getTime())) {
      throw new CanonicalJsonError('Ungültiges Datum', pfad);
    }
    return schreibeString(wert.toISOString());
  }

  if (typeof wert === 'object') {
    if (gesehen.has(wert)) {
      throw new CanonicalJsonError('Zirkuläre Referenz', pfad);
    }
    gesehen.add(wert);
    try {
      if (Array.isArray(wert)) {
        const teile = wert.map((eintrag, index) => {
          const serialisiert = serialisiere(eintrag, `${pfad}[${index}]`, gesehen);
          // Lücken in Arrays werden zu null, sonst verschöbe sich der Index.
          return serialisiert ?? 'null';
        });
        return `[${teile.join(',')}]`;
      }

      if (!istEinfachesObjekt(wert)) {
        throw new CanonicalJsonError(
          `Nicht unterstützter Objekttyp: ${Object.prototype.toString.call(wert)}`,
          pfad,
        );
      }

      const schluessel = Object.keys(wert).sort(vergleicheCodepoints);
      const teile: string[] = [];
      for (const key of schluessel) {
        const serialisiert = serialisiere(wert[key], pfad ? `${pfad}.${key}` : key, gesehen);
        if (serialisiert === undefined) continue;
        teile.push(`${schreibeString(key)}:${serialisiert}`);
      }
      return `{${teile.join(',')}}`;
    } finally {
      gesehen.delete(wert);
    }
  }

  return undefined;
};

/**
 * Sortiert nach Unicode-Codepoints statt nach Locale.
 * `Array.prototype.sort()` ohne Comparator sortiert nach UTF-16-Codeunits —
 * das weicht bei Zeichen jenseits der BMP von der Codepoint-Reihenfolge ab.
 */
const vergleicheCodepoints = (a: string, b: string): number => {
  const aCp = [...a];
  const bCp = [...b];
  const laenge = Math.min(aCp.length, bCp.length);
  for (let i = 0; i < laenge; i += 1) {
    const cpA = aCp[i]!.codePointAt(0)!;
    const cpB = bCp[i]!.codePointAt(0)!;
    if (cpA !== cpB) return cpA - cpB;
  }
  return aCp.length - bCp.length;
};

/**
 * Serialisiert einen Wert kanonisch. Ergebnis ist byte-stabil über Geräte,
 * Laufzeiten und Zeit hinweg.
 */
export const canonicalJson = (wert: unknown): string => {
  const ergebnis = serialisiere(wert, '', new Set());
  if (ergebnis === undefined) {
    throw new CanonicalJsonError('Wert ist nicht serialisierbar', '');
  }
  return ergebnis;
};
